import React from 'react';
import PageHeader from '../ui/PageHeader';
import { CONTENT_WIDTH, PAGE_STACK, TEXT } from '../../uiTokens';

function Section({ id, title, children }) {
  return (
    <section
      id={id}
      aria-labelledby={`${id}-title`}
      className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,2.4fr)] gap-3 md:gap-12 py-8 sm:py-10 border-b border-line"
    >
      <h2 id={`${id}-title`} className={TEXT.h3}>{title}</h2>
      <div className={`${TEXT.body} flex flex-col gap-4`}>{children}</div>
    </section>
  );
}

export default function PrivacyPage() {
  return (
    <div className={`${CONTENT_WIDTH} ${PAGE_STACK}`}>
      <PageHeader
        eyebrow="Legal"
        title="Privacy Policy"
        description="What we collect, what we never see, and how data is handled across the website, API, and CLI."
        aside={<p className={TEXT.caption}>Last updated September 1, 2026</p>}
      />

      <div className="mt-10 sm:mt-14 border-t border-line">
        <Section id="overview" title="Overview">
          <p>
            Hushify helps you share secrets through one-time links. This policy explains what we collect and how we handle data when you use the website, API, or CLI.
          </p>
        </Section>

        <Section id="data" title="Data we collect">
          <div>
            <h3 className={`${TEXT.label} mb-1`}>Analytics</h3>
            <p>
              We may use Cloudflare Web Analytics for aggregated, anonymous usage data (page views, browser type, country-level location). This never includes secret content.
            </p>
          </div>
          <div>
            <h3 className={`${TEXT.label} mb-1`}>Secret content</h3>
            <p>
              In the web app, secrets are encrypted in your browser before upload. The decryption key stays in the link you share - our servers never receive it. We store only the encrypted version, and delete it after expiry or after the link is opened once.
            </p>
          </div>
          <div>
            <h3 className={`${TEXT.label} mb-1`}>Optional settings</h3>
            <p>
              If you turn on email notifications or IP restrictions, we store the email address and allowed IP information tied to that link - never the secret itself.
            </p>
          </div>
        </Section>

        <Section id="use" title="How we use data">
          <ul className="flex flex-col gap-2 list-disc pl-5">
            <li>Create and open secret links</li>
            <li>Send email notifications when you opt in</li>
            <li>Enforce IP restrictions when you configure them</li>
            <li>Keep the service reliable and prevent abuse</li>
          </ul>
        </Section>

        <Section id="cookies" title="Cookies">
          <p>
            Cookies are limited to analytics where enabled. Your theme preference is stored in localStorage on your device.
          </p>
        </Section>

        <Section id="security" title="Security">
          <p>
            Web sharing is end-to-end encrypted: only someone with the complete link can read the secret. Our team cannot read your secrets. Slack and Discord entry points encrypt on the server when the link is created - see product docs for details per integration.
          </p>
        </Section>

        <Section id="rights" title="Your rights">
          <p>
            You can decline analytics cookies where offered. Because secrets are encrypted in your browser and the key stays in the link, we cannot recover secret content after you create a link.
          </p>
        </Section>

        <Section id="contact" title="Contact">
          <p>
            Questions about this policy:{' '}
            <a href="mailto:privacy@nvteh.com" className={TEXT.link}>privacy@nvteh.com</a>
          </p>
        </Section>
      </div>
    </div>
  );
}
